import {
  AccessTimeOutlined,
  ArchiveOutlined,
  ChatOutlined,
  DescriptionOutlined,
  FlagOutlined,
  FolderOutlined,
  ImageOutlined,
  LanguageOutlined,
  LibraryBooksOutlined,
  MiscellaneousServicesOutlined,
  PhotoAlbumOutlined,
  QuestionAnswerOutlined,
  TranslateOutlined,
  VideoLibraryOutlined,
} from "@mui/icons-material";
import AudiotrackOutlinedIcon from "@mui/icons-material/AudiotrackOutlined";
import SubscriptionsOutlinedIcon from "@mui/icons-material/SubscriptionsOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import InfoIcon from "@mui/icons-material/Info";
import { Chip, Grid2 as Grid, Icon, Link, Paper, Stack, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router";
import AudioTracksExcel from "../../../../../Components/AudioTracks/audio-tracks-excel";
import MetadataBoxAccordion from "../../../../../Components/Misc/Metadata/MetadataBoxAccordion";
import MetadataListChipListItem from "../../../../../Components/Misc/Metadata/MetadataListChipListItem";
import MetadataListTextItem from "../../../../../Components/Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import PosterImage from "../../../../../Components/Misc/PosterImage";
import TableSkeleton from "../../../../../Components/Table/TableSkeleton";
import { assetService, cacheService, ingestService, thumbnailService } from "../../../../../Core/Api/services";
import { ThumbnailSize } from "../../../../../Core/Api/Thumbnail/thumbnail-api-service";
import { ObjectType } from "../../../../../Core/Api/Types/object-type";
import { GetObject, GetObjectMetadataMetadataDto } from "../../../../../Redux/Reducers/redux-slice-asset";
import { useAppSelector } from "../../../../../Redux/redux-store";
import { routePaths } from "../../../../../Router/router";
import { GetTitleRouterPath, UserHasAcl, LastIndex } from "../../../../../Utils/helpers";
import AssetClassChip from "../../../../../Components/Misc/Chips/AssetChip/asset-class-chip";
import GlobalConst from "../../../../../Core/Utils/Constants/globals";
import moment from "moment";
import { AclTypes } from "../../../../../Core/Api/Types/acl-types";
import { ObjectLayer } from "../../../../../Core/Api/Types/object-layer";
import { GetMetadataByTagName } from "../../../../../Core/Utils/asset-object-utils";
import { PlatformMode } from "../../../../../Core/Api/Types/platform-mode";
import { ObjectClass } from "../../../../../Core/Api/Types/object-class";
import "material-icons/iconfont/material-icons.css";
import AssetTypeFromObjectChip from "../../../../../Components/Misc/Chips/AssetChip/asset-type-chip-from-object";
import AudioTracksList from "../../../../../Components/AudioTracks/audio-tracks-list";
import { useTranslation } from "react-i18next";

function AssetSingleMetadata() {
  const { t } = useTranslation();
  const assetData = useAppSelector(GetObjectMetadataMetadataDto);
  const assetNative = useAppSelector(GetObject);
  const aclPlaceholder = UserHasAcl(AclTypes.aclIngestPlaceholderCreate);
  const aclSuperUser = UserHasAcl(AclTypes.aclSuperUser);
  const [placeholder, setPlaceholder] = useState("");

  useEffect(() => {
    if (!assetNative?.placeholderRowId || !aclPlaceholder) return;

    ingestService.GetPlaceholderById(assetNative?.placeholderRowId!).then((response) => {
      setPlaceholder(response.placeholder?.name ?? "");
    });
  }, [aclPlaceholder, assetNative]);

  const acceptanceDate = useMemo(() => {
    if (!assetNative || !assetNative.statusHistory) return null;

    let index = assetNative.statusHistory
      .slice()
      .reverse()
      .findIndex((x) => x.status === "Accepted");

    let count = assetNative.statusHistory.length - 1;
    index = index >= 0 ? count - index : index;

    if (index >= 0) return assetNative.statusHistory[index].date;
    else return null;
  }, [assetNative]);

  const ingestDate = useMemo(() => {
    if (!assetNative || !assetNative.statusHistory) return null;

    let index = LastIndex(assetNative.statusHistory, "status", "ParsedAndProbbed");

    if (index >= 0) return assetNative.statusHistory[index].date;
    else return acceptanceDate;
  }, [assetNative, acceptanceDate]);

  return (
    <>
      {!assetData ? (
        <TableSkeleton />
      ) : (
        <>
          <Paper sx={{ backgroundImage: "none", p: 3, mb: 3, mt: 2 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              {/* TODO : maybe get default image when no parent (open case) */}
              {assetData.parentTitleId && (
                <Link to={`${GetTitleRouterPath()}/${assetData.parentTitleId}`} component={RouterLink}>
                  <PosterImage
                    img={{ src: thumbnailService.GetObjectPosterUrl(assetData.parentTitleId, ThumbnailSize.small) }}
                    height={108}
                    width={80}
                    sx={{ cursor: "pointer" }}
                  />
                </Link>
              )}
              <Stack spacing={1} flex={1}>
                {assetData.parentTitleLayer === ObjectLayer.Title && assetData.parentTitleId && (
                  <MetadataListTextItem
                    wrapperProps={{ justifyContent: "flex-start" }}
                    titleProps={{ flex: 0.15 }}
                    title={t("common:title")}
                    flex={0.8}
                    textAlign="left"
                  >
                    {assetData.parentTitleId ? (
                      <Link
                        underline="hover"
                        fontSize={14}
                        fontWeight={700}
                        to={`${GetTitleRouterPath()}/${assetData.parentTitleId}`}
                        component={RouterLink}
                        color="text.primary"
                      >
                        <Typography fontWeight={600}>{assetData.parentTitleName}</Typography>
                      </Link>
                    ) : (
                      assetData.parentTitleName
                    )}
                  </MetadataListTextItem>
                )}
                <MetadataListTextItem
                  wrapperProps={{ justifyContent: "flex-start" }}
                  titleProps={{ flex: 0.15 }}
                  title={t("menu:title.parentContent")}
                  fontWeight={600}
                  flex={0.8}
                  textAlign="left"
                >
                  {assetData.parentObjectId && (
                    <Link
                      underline="hover"
                      fontSize={14}
                      fontWeight={700}
                      to={`${assetData.class !== ObjectClass.Drive ? routePaths.asset.path : routePaths.drive.path}/${
                        assetData.parentObjectId
                      }`}
                      component={RouterLink}
                      color="text.primary"
                    >
                      <Typography fontWeight={600}>{assetData.parentObjectName}</Typography>
                    </Link>
                  )}
                </MetadataListTextItem>
                <MetadataListTextItem
                  wrapperProps={{ justifyContent: "flex-start" }}
                  titleProps={{ flex: 0.15 }}
                  title={t("menu:title.fullName")}
                  flex={0.8}
                  textAlign="left"
                >
                  {assetData.name}
                </MetadataListTextItem>
                <MetadataListChipListItem
                  title={t("common:type")}
                  wrapperProps={{ justifyContent: "flex-start" }}
                  titleProps={{ flex: 0.15 }}
                  contentProps={{ flex: 0.8 }}
                  ml={"-2px !important"}
                  justifyContent="flex-start"
                >
                  {assetNative && <AssetTypeFromObjectChip obj={assetNative} />}
                  {assetData.class && <AssetClassChip classification={assetData.class} />}
                  {assetNative?.parentTitle &&
                    cacheService.GetPlatformMode() === PlatformMode.Distribution &&
                    assetData.class != ObjectClass.Drive && (
                      <Chip
                        label={t("chip:label.titleOar", {
                          title: GetMetadataByTagName(assetNative?.parentTitle, "titleOriginalPictureFormat")?.value,
                        })}
                      ></Chip>
                    )}
                  {assetNative?.parentTitle &&
                    aclSuperUser &&
                    cacheService.GetPlatformMode() === PlatformMode.Distribution &&
                    assetData.class != ObjectClass.Drive && (
                      <Chip
                        label={t("chip:label.titleLanguage", {
                          titleLabel: GetMetadataByTagName(assetNative?.parentTitle, "titleOriginalLanguage")?.value
                            .label,
                          titleValue: GetMetadataByTagName(assetNative?.parentTitle, "titleOriginalLanguage")?.value
                            .value,
                        })}
                      ></Chip>
                    )}
                </MetadataListChipListItem>
              </Stack>
            </Stack>
          </Paper>
          <Grid container spacing={2}>
            <Grid size={6}>
              {assetNative?.layer === ObjectLayer.Asset && (
                <MetadataBoxAccordion
                  key={"static-section"}
                  title={t("common:generalInformations")}
                  icon={<InfoIcon />}
                >
                  <Stack spacing={1}>
                    <MetadataListTextItem title={t("common:upperCaseCreation")}>
                      {assetNative?.creationInfos?.userName},{" "}
                      {moment(assetNative?.creationInfos?.date).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
                    </MetadataListTextItem>
                    <MetadataListTextItem title={t("menu:title.ingestDate")}>
                      {ingestDate ? moment(ingestDate).format(GlobalConst.MOMENT_DATE_FORMAT_FULL) : t("common:notYet")}
                    </MetadataListTextItem>
                    <MetadataListTextItem title={t("menu:title.acceptanceDate")}>
                      {acceptanceDate
                        ? moment(acceptanceDate).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)
                        : t("common:notYet")}
                    </MetadataListTextItem>
                    <MetadataListTextItem title={t("menu:title.providerAssigned")}>
                      <Chip color="info" label={assetNative?.providerName ?? t("chip:label.notAssigned")}></Chip>
                    </MetadataListTextItem>
                    <MetadataListTextItem title={t("common:followers")}>
                      {assetNative?.followedByUserNames ?? t("menu:item.noFollowers")}
                    </MetadataListTextItem>
                    {aclPlaceholder && placeholder !== "" && (
                      <MetadataListTextItem title={t("common:placeholderName")}>{placeholder}</MetadataListTextItem>
                    )}
                  </Stack>
                </MetadataBoxAccordion>
              )}
              {assetNative?.layer === ObjectLayer.Content && (
                <MetadataBoxAccordion
                  key={"static-section"}
                  title={t("common:generalInformations")}
                  icon={<InfoIcon />}
                >
                  <Stack spacing={1}>
                    <MetadataListTextItem title={t("common:upperCaseCreation")}>
                      {assetNative?.creationInfos?.userName},{" "}
                      {moment(assetNative?.creationInfos?.date).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
                    </MetadataListTextItem>
                  </Stack>
                </MetadataBoxAccordion>
              )}
              {assetData.metadatas.sections.map((section, sectionIndex) => {
                if (sectionIndex % 2 === 0)
                  return (
                    <MetadataBoxAccordion
                      key={sectionIndex}
                      title={section.name}
                      icon={<NewMetadataIcon icon={section.icon} />}
                    >
                      <Stack spacing={1}>
                        {section.rows.map((row, rowIndex) => (
                          <MetadataListTextItem key={rowIndex} title={row.label}>
                            {assetService.FormatMetadataRow(row) ?? "-"}
                          </MetadataListTextItem>
                        ))}
                      </Stack>
                    </MetadataBoxAccordion>
                  );
              })}
            </Grid>
            <Grid size={6}>
              {assetData.metadatas.sections.map((section, sectionIndex) => {
                if (sectionIndex % 2 === 1)
                  return (
                    <MetadataBoxAccordion
                      key={sectionIndex}
                      title={section.name}
                      icon={<NewMetadataIcon icon={section.icon} />}
                    >
                      <Stack spacing={1}>
                        {section.rows.map((row, rowIndex) => (
                          <MetadataListTextItem key={rowIndex} title={row.label}>
                            {assetService.FormatMetadataRow(row) ?? "-"}
                          </MetadataListTextItem>
                        ))}
                      </Stack>
                    </MetadataBoxAccordion>
                  );
              })}
              {(assetNative?.type === ObjectType.Video || assetNative?.type === ObjectType.Audio) && (
                <MetadataBoxAccordion
                  key="audiotracks"
                  title={t("page:metadataBoxAccordion.title.audioChannels")}
                  icon={<AudiotrackOutlinedIcon />}
                >
                  <Stack spacing={1}>
                    {cacheService.GetPlatformMode() === PlatformMode.Adam ||
                    cacheService.GetUserInfo()?.prefs?.audioTracksList ||
                    cacheService.GetPlatformSettings()?.prefsDefault?.audioTracksList ? (
                      <AudioTracksList asset={assetNative!} />
                    ) : (
                      <AudioTracksExcel asset={assetNative!} />
                    )}
                  </Stack>
                </MetadataBoxAccordion>
              )}
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}

// Keep this until below todos are not done => there is relevant icons
{
  /* <MetadataBoxAccordion title="Asset Summary" icon={<SummarizeOutlinedIcon />}></MetadataBoxAccordion>
  <MetadataBoxAccordion title="Video infos" icon={<VideoLibraryOutlinedIcon />}></MetadataBoxAccordion>
  <MetadataBoxAccordion title="Media infos" icon={<SubscriptionsOutlinedIcon />}>
  <MetadataBoxAccordion title="Audio infos" icon={<AudiotrackOutlinedIcon />}>
  <MetadataBoxAccordion title="Logo infos" icon={<AudiotrackOutlinedIcon />}>
  <MetadataBoxAccordion title="Ingest History" icon={<SubscriptionsOutlinedIcon />}>
  <MetadataBoxAccordion title="Localization" icon={<LanguageOutlinedIcon />}>
  <MetadataBoxAccordion title="Timecode" icon={<VideoSettingsOutlinedIcon />}>
  <MetadataBoxAccordion title="Audio track mapping" icon={<AudioFileOutlinedIcon />}> */
}

// new mapping of angular icons, icons are more relevant
export const NewMetadataIcon: FC<{ icon: string; fontSize?: "inherit" | "small" | "medium" | "large" }> = ({
  icon,
  fontSize = "inherit",
}) => {
  // TODO : find a way to get mui icons dynamically (<Icon> exists but needs to import ALL icons, super heavy) / or not dynamically but programmaticaly import them (to avoid importing all of them)
  // TODO : Change previous icons in db by new ones => some are not relevant, ex below : we have comment for localization...
  // For metadatas views, maybe define patterns that are heavily used actually (asset summary, media format etc etc ) and let users the ability to create new ones (custom ones)
  switch (icon) {
    case "zmdi zmdi-movie m-r-5":
      return <SummarizeOutlinedIcon fontSize={fontSize} />;
    case "zmdi zmdi-wrench m-r-5":
      return <SubscriptionsOutlinedIcon fontSize={fontSize} />;
    case "zmdi zmdi-globe-alt m-r-5":
      return <LanguageOutlined fontSize={fontSize} />;
    case "zmdi zmdi-speaker m-r-5":
      return <AudiotrackOutlinedIcon fontSize={fontSize} />;
    case "zmdi zmdi-comments m-r-5":
      return <LanguageOutlined fontSize={fontSize} />;
    case "zmdi zmdi-local-movies m-r-5":
      return <VideoLibraryOutlinedIcon fontSize={fontSize} />;

    case "zmdi zmdi-collection-text m-r-5":
      return <LibraryBooksOutlined fontSize={fontSize} />;
    case "zmdi zmdi-time m-r-5":
      return <AccessTimeOutlined fontSize={fontSize} />;
    case "zmdi zmdi-folder m-r-5":
      return <FolderOutlined fontSize={fontSize} />;
    case "zmdi zmdi-collection-video m-r-5":
      return <VideoLibraryOutlined fontSize={fontSize} />;
    case "zmdi zmdi-book-image m-r-5":
      return <PhotoAlbumOutlined fontSize={fontSize} />;
    case "zmdi zmdi-file-text":
      return <DescriptionOutlined fontSize={fontSize} />;
    case "zmdi zmdi-image m-r-5":
      return <ImageOutlined fontSize={fontSize} />;
    case "zmdi zmdi-translate":
      return <TranslateOutlined fontSize={fontSize} />;
    case "zmdi zmdi-flag m-r-5":
      return <FlagOutlined fontSize={fontSize} />;
    case "zmdi zmdi-comments m-r-5":
      return <QuestionAnswerOutlined fontSize={fontSize} />;
    case "zmdi zmdi-comment-text m-r-5":
      return <ChatOutlined fontSize={fontSize} />;
    case "zmdi zmdi-archive m-r-5":
      return <ArchiveOutlined fontSize={fontSize} />;
    case "":
      return <MiscellaneousServicesOutlined />;
    //return <VideoSettingsOutlinedIcon />;
    default:
      return <Icon fontSize={fontSize}>{icon}</Icon>;
  }
};

export default AssetSingleMetadata;
